/**
 * 텍스트형 PPT 크게 보기(발표) — 화면 가득 한 장씩. ←/→/스페이스로 넘기고 Esc 로 닫는다.
 * 대본이 있으면 「대본」으로 슬라이드 아래에 펼친다 (슬라이드는 남은 높이에 맞춰 줄어든다).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FreeDesignSlideFrame, { SLIDE_H, SLIDE_W } from './FreeDesignSlideFrame';
import './FreeDesignDeck.css';

const NOTES_KEY = 'wqfd-notes-open';
const BAR_H = 72;
const NOTES_H = 180;

export const readNotesOpen = () => {
  try {
    return window.localStorage.getItem(NOTES_KEY) === '1';
  } catch (_error) {
    return false;
  }
};

export const writeNotesOpen = (open) => {
  try {
    window.localStorage.setItem(NOTES_KEY, open ? '1' : '0');
  } catch (_error) {
    // 저장소를 못 쓰면 이번 화면에서만 기억한다
  }
};

const fitWidth = (withNotes) => {
  if (typeof window === 'undefined') return SLIDE_W;
  const w = window.innerWidth;
  const h = window.innerHeight - BAR_H - (withNotes ? NOTES_H : 0);
  return Math.max(240, Math.min(w, (h * SLIDE_W) / SLIDE_H));
};

const FreeDesignPresent = ({
  head, slides, initialIndex = 0, onClose, total = null, notesOpen: notesOpenProp, onToggleNotes,
}) => {
  const [idx, setIdx] = useState(() => Math.max(0, Math.min(initialIndex, slides.length - 1)));
  const [ownNotesOpen, setOwnNotesOpen] = useState(readNotesOpen);
  const hasNotes = slides.some((s) => s?.notes);
  const notesOpen = hasNotes && (notesOpenProp ?? ownNotesOpen);
  const [width, setWidth] = useState(() => fitWidth(notesOpen));
  const rootRef = useRef(null);
  const idxRef = useRef(idx);
  idxRef.current = idx;

  const close = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    onClose?.(idxRef.current);
  }, [onClose]);

  const go = useCallback((delta) => {
    setIdx((i) => Math.max(0, Math.min(slides.length - 1, i + delta)));
  }, [slides.length]);

  const toggleNotes = () => {
    if (onToggleNotes) {
      onToggleNotes();
      return;
    }
    writeNotesOpen(!ownNotesOpen);
    setOwnNotesOpen(!ownNotesOpen);
  };

  useEffect(() => {
    setWidth(fitWidth(notesOpen));
    const onResize = () => setWidth(fitWidth(notesOpen));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [notesOpen]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') close();
      else if (['ArrowRight', 'PageDown', ' ', 'Enter'].includes(event.key)) { event.preventDefault(); go(1); }
      else if (['ArrowLeft', 'PageUp', 'Backspace'].includes(event.key)) { event.preventDefault(); go(-1); }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    rootRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [close, go]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else rootRef.current?.requestFullscreen?.().catch(() => {});
  };

  const slide = slides[idx];
  return createPortal(
    <div
      aria-label="슬라이드 크게 보기"
      aria-modal="true"
      className="wqfd-present"
      data-testid="wqfd-present-view"
      ref={rootRef}
      role="dialog"
      tabIndex={-1}
    >
      <div className="wqfd-present__bar">
        <span className="wqfd-present__pos">{`${slide?.num ?? idx + 1} / ${total ?? slides.length}`}</span>
        <div className="wqfd-present__bar-actions">
          {hasNotes && (
            <button
              aria-pressed={notesOpen}
              className={`wqfd-present__btn${notesOpen ? ' is-on' : ''}`}
              data-testid="wqfd-present-notes-toggle"
              onClick={toggleNotes}
              type="button"
            >
              대본
            </button>
          )}
          <button className="wqfd-present__btn" onClick={toggleFullscreen} type="button">전체 화면</button>
          <button className="wqfd-present__btn" data-testid="wqfd-present-close" onClick={close} type="button">닫기</button>
        </div>
      </div>
      <div className="wqfd-present__body">
        <button aria-label="이전 슬라이드" className="wqfd-present__nav wqfd-present__nav--prev" disabled={idx === 0} onClick={() => go(-1)} type="button">‹</button>
        <div className="wqfd-present__slide" style={{ width }}>
          {slide && <FreeDesignSlideFrame head={head} html={slide.html} key={slide.id} title={`${slide.num ?? idx + 1}번 슬라이드`} />}
        </div>
        <button aria-label="다음 슬라이드" className="wqfd-present__nav wqfd-present__nav--next" disabled={idx >= slides.length - 1} onClick={() => go(1)} type="button">›</button>
      </div>
      {notesOpen && (
        <div className="wqfd-present__notes" data-testid="wqfd-present-notes" style={{ height: NOTES_H }}>
          <p className="wqfd-present__notes-text">{slide?.notes || '이 장은 대본이 없어요'}</p>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default FreeDesignPresent;
